/** @jsx jsx */ /** @jsxRuntime classic */
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box, Input, Text, Button } from 'theme-ui';
import { jsx } from 'theme-ui';
import dropdownArrow from './images/dropdown-arrow.svg';

// make things Normal
const normalizeOptions = (options) =>
  (options || []).map((opt) => {
    if (typeof opt === 'string' || typeof opt === 'number') {
        return { value: String(opt), label: String(opt) };
    }
    const v = opt?.value ?? opt?.id ?? opt; 
    const l = opt?.label ?? opt?.name ?? v;  
    return { value: String(v), label: String(l) };
  });
const ensureArray = (v) => (Array.isArray(v) ? v : (v == null ? [] : [v]));

const ProfileInput = ({label, field, placeholder, value, onChange, options, multiple,}) => {
    const normalized = useMemo(() => normalizeOptions(options), [options]);
    const hasSelection = multiple
    ? ensureArray(value).length > 0
    : Boolean(value ?? '');

    // remove chip from multiselect
    const removeChip = (val) => {
        const next = ensureArray(value).filter((v) => v !== val);
        onChange(field, next);
    };

    // create custom multiselect state vars and refs
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    // close the dropdown
    useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
        if (
            triggerRef.current && !triggerRef.current.contains(e.target) && menuRef.current && !menuRef.current.contains(e.target)
        ) {
            setOpen(false);
        }
    };
    const onKey = (e) => {
    if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
        document.removeEventListener('mousedown', onDocClick);
        document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        {/* selection label (name, occupation) */},
        <Box sx={{ width: '100%', mb: 3 }}>
        {label && (
            <Text
            sx={{
                fontWeight: 'bold',
                fontSize: '14px',
                lineHeight: '18px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                mb: 2,
                padding: '0 10px',
            }}
            >
        {label}
        </Text>
    )}

    {options ? (
        {/* chips for multiple rows */},
        <Box sx={{ position: 'relative', width: '100%' }}>
        {multiple && (
            <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: '8px',
            }}
            aria-live="polite"
            >
            {ensureArray(value).map((val) => {
                const opt = normalized.find((o) => o.value === val);
                const chipLabel = opt?.label ?? val;
                {/* THIS IS CHIP PADDING. CHANGE CHIP PADDING HERE. (keep forgetting where it is)*/}
                return (
                <Box
                    key={val}
                    sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    marginTop: '6px',
                    padding: "7px 15px", 
                    borderRadius: '20px',
                    bg: '#d5b3e7',
                    fontSize: 1,
                    }}
                >
                    {chipLabel}
                    {/* removing chips */}
                    <Button
                        onClick={() => removeChip(val)}
                        aria-label={`Remove ${chipLabel}`}
                        sx={{
                            borderRadius: '20px',
                            px: 0.5,
                            py: 0,
                            lineHeight: 1,
                            bg: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#333',
                            minWidth: 'auto',
                        }}
                        >
                        ×
                    </Button>
                </Box>
                );
            })}
        </Box>
        )}

        {multiple ? (
            <>
            {/* make multi select */}
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open ? 'true' : 'false'}
                style={{
                    border: '2px solid #CCC7C7',
                    borderRadius: '20px',
                    minHeight: '3em',
                    marginTop: '6px',
                    padding: '0.75em 2.75em 0.75em 1em',
                    width: '100%',
                    fontSize: '16px',
                    backgroundColor: 'white',
                    color: '#333',
                    textAlign: 'left',
                    position: 'relative',
                    cursor: 'pointer',
                }}
            >
                <span style={{ opacity: hasSelection ? 1 : 0.6 }}>
                    {hasSelection
                    ? ensureArray(value)
                        .map(
                            (v) => normalized.find((o) => o.value === v)?.label ?? v).join(', ')
                            : placeholder || 'Select one or more…'}
                </span>

                <span
                aria-hidden="true"
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '44px',
                        height: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                <img src={dropdownArrow} alt="" width="15" height="15" />
                </span>
              </button>

            {open && (
                <div
                    ref={menuRef}
                    role="listbox"
                    aria-multiselectable="true"
                    style={{
                        position: 'absolute',
                        zIndex: 10,
                        marginTop: '6px',
                        width: '100%',
                        maxHeight: '14rem',
                        overflow: 'auto',
                        background: 'white',
                        border: '2px solid #CCC7C7',
                        borderRadius: '14px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        fontFamily: 'Poppins'
                    }}
                >
                {normalized.map(({ value: val, label: lab }) => {
                    const selected = ensureArray(value).includes(val);
                    return (
                        <div key={val} role="option" aria-selected={selected ? 'true' : 'false'} onClick={() => {
                            const cur = new Set(ensureArray(value));
                            if (cur.has(val)) cur.delete(val);
                            else cur.add(val);
                            onChange(field, Array.from(cur));
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            fontSize: '16px',
                            fontFamily: 'inherit',
                            backgroundColor: selected ? '#dfebff' : 'transparent',
                            transition: 'background-color 0.1s ease-in-out',
                            '&:hover': {backgroundColor: 'rgba(0,0,0,0.8)',}
                        }}
                    >
                        {/* thoughts? i think it is easier to see but maybe it looks stupid*/}
                        <input
                            type="checkbox"
                            checked={selected}
                            readOnly
                            style={{ pointerEvents: 'none' }}
                            aria-hidden="true"
                        />
                        <span>{lab}</span>
                    </div>
                    );
                })}
                </div>
            )}

            <select
                multiple
                value={ensureArray(value)}
                onChange={() => {}}
                style={{ display: 'none' }}
                tabIndex={-1}
                aria-hidden="true"
            >
                {normalized.map(({ value: v, label: l }) => (
                    <option key={v} value={v}>
                        {l}
                    </option>
                ))}
            </select>
            </>
        ) : (
    <>
    {/* single select */}
    <button ref={triggerRef} type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open ? 'true' : 'false'}
        style={{
            border: '2px solid #CCC7C7',
            borderRadius: '20px',
            padding: '0.75em 2.75em 0.75em 1em',
            width: '100%',
            fontSize: '16px',
            backgroundColor: 'white',
            color: '#333',
            marginTop: '6px',
            position: 'relative',
            cursor: 'pointer',
            lineHeight: '1.3',
            whiteSpace: 'normal',
            minHeight: '3em',
            display: 'flex',
            alignItems: 'center',
        }}
    >
    <span style={{ opacity: hasSelection ? 1 : 0.6 }}>
        {hasSelection
            ? (normalized.find((o) => o.value === String(value))?.label ?? String(value))
            : (placeholder || 'Select an option')}
    </span>

    <span
        aria-hidden="true"
        style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '44px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
        }}
    >
        <img src={dropdownArrow} alt="" width="15" height="15" />
        </span>
    </button>

    {open && (<div
        ref={menuRef}
        role="listbox"
        aria-multiselectable="false"
        style={{
            position: 'absolute',
            zIndex: 10,
            marginTop: '6px',
            width: '100%',
            maxHeight: '14rem',
            overflow: 'auto',
            background: 'white',
            border: '2px solid #CCC7C7',
            borderRadius: '14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
        >
        {normalized.map(({ value: v, label: l }) => {
            const selected = String(value ?? '') === v;
            return (
            <Box
                key={v}
                role="option"
                aria-selected={selected ? 'true' : 'false'}
                onClick={() => {
                    onChange(field, v);
                    setOpen(false);
                }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 3,
                py: 2,
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: 2,
                backgroundColor: selected ? '#dfebff' : 'transparent',
                transition: 'background-color 0.1s ease-in-out',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
            }}
            >
            <span>{l}</span>
            </Box>
        );
        })}
    </div>
    )}

    {/* keeping original select for accessibility purposes */}
    <select value={hasSelection ? String(value) : ''}
        onChange={() => {}}
        style={{ display: 'none' }}
        tabIndex={-1}
        aria-hidden="true"
    >
    {!hasSelection && (
        <option value="">{placeholder || 'Select an option'}</option>
    )}
        {normalized.map(({ value: v, label: l }) => (
        <option key={v} value={v}>{l}</option>
    ))}
    </select>
    </>
)}
    </Box>
    ) : (
        // text input (NOT selection)
        <Input
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(field, e.target.value)}
            sx={{
                border: '2px solid #CCC7C7',
                borderRadius: '20px',
                height: '3em',
                paddingLeft: '1em',
                width: '100%',
                marginTop: '6px',
                fontFamily: 'Poppins',
            }}
        />
    )}
    </Box>
    );
};

export default ProfileInput;
